import { Component, EventEmitter, OnInit, Output, Input } from '@angular/core';
import * as moment from 'moment';
import {AbstractControl, FormBuilder, FormGroup} from '@angular/forms';
import { RemitoAcopio } from '../acopio-selector-dialog';
import {
  RemitosService,
  CentroEmisor,
  AcopioNuevo,
} from '@services/remitos-service';
import { ColaboradoresService } from '@services/colaboradores-service';
import { ABMRolesService } from '@services/abm-roles-service';
import { MdSnackBar } from '@angular/material';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {FormSelectItem} from '@common/form-components';
import {ActivatedRoute, Router} from '@angular/router';
import {Observable} from 'rxjs/Observable';
import {Location} from '@angular/common';
import {HttpParams} from '@angular/common/http';

@Component({
  selector: 'app-remitos-fecha-selector',
  templateUrl: './remitos-fecha-selector.component.html',
  styleUrls: [
    './remitos-fecha-selector.component.scss',
    '../remitos-common-styles.scss',
  ],
})
export class RemitosFechaSelectorComponent {
  week: DayObj[] = [];
  selectedDay: DayObj;
  max = moment().toDate();

  matDatepickerDate;

  @Output() filterChanged = new EventEmitter<RemitoRequiredFilterObj>();
  @Output() invalidFilter = new EventEmitter();

  _isAdmin: boolean = false;
  @Input() set isAdmin(value: boolean) {
    this._isAdmin = coerceBooleanProperty(value);
    // los admin pueden modificar estos filtros, por lo que si no lo es, lo deshabilitamos
    if (this._isAdmin) {
      if (!this.formInitializing) {
        this.form.enable({onlySelf: false, emitEvent: true});
      }
    } else {
      this.form.disable({onlySelf: false, emitEvent: true});
    }
  }

  jdeData = []

  form: FormGroup;
  formInitializing = false;

  listaAcopios: FormSelectItem[] = [];
  loadingOffice: boolean = true;
  listaCentrosEmisores: FormSelectItem[] = [];
  centrosEmisoresFiltered: any[] = [];
  emisoresLoading = false;
  constructor(
    private fb: FormBuilder,
    private remitosService: RemitosService,
    private colaboradorService: ColaboradoresService,
    private abmRolesService: ABMRolesService,
    private snackBar: MdSnackBar,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
  ) {
    this.form = this.fb.group({
      acopioSelected: [null],
      centroEmisorSelected: [null],
    });

    // nos registramos al cambio de acopio y buscamos el centro emisor
    this.acopioSelectedCtrl.valueChanges.subscribe(x => {
      const val = this.acopioSelected;
      if (val) {
        this.searchCentroEmisor(Number((val as FormSelectItem).Id));
      }
    });

    // al registrarse un cambio de centro emisor, enviamos un evento submit
    this.centroEmisorSelectedCtrl.valueChanges.subscribe(x => {
      this.onSubmit();
    });

    this.initializeAllFilters();
  }

  initializeAllFilters() {
    // en esta funcion se usa el try catch dado que hay muchos pasos y formas en las que esto puede llegar a fallar.
    // se usa ese approach para evitar que el codigo continue su camino y
    // en lugar de eso se pase al filtro por defecto sin mayor inconveniente

    // primero, evitamos que el usuario edite durante la carga
    this.form.disable({onlySelf: false, emitEvent: false});
    this.formInitializing = true;

    // para inicializar todos los filtros debemos primero obtener JDE y Acopios
    // combineLatest se suscribirá y esperará a que todos los observables se completen y devuelve las respuestas en un array
    const jdeData = this.colaboradorService.getUserJDEAccess();
    const acopios = this.remitosService.$acopiosAll;

    Observable.combineLatest([jdeData, acopios]).subscribe(
      result => {
        // las respuestas estan en el orden en que se generaron
        this.jdeData = result[0];
        this.listaAcopios = result[1].map(y => ({
          Id: y.IdAcopio,
          Label: y.Nombre
        }));
        // con esta informacion, verificamos si hay un filtro en la URL
        this.route.queryParams.subscribe(x => {
          if (Object.keys(x).length > 0) {
            // hay filtros, los levantamos desde la URL.
            // vamos a hacer una conversion de la URL tipo ANY a un objeto tipado
            // si hay algun error de conversion lo manejamos con el try catch
            // tambien si hay alguna condicion necesaria que no se cumple podemos salir de dicha seccion de codigo
            try {
              // ----- params
              const params = {...x} as RemitoFilterParams;
              if (!params.f || !params.a || !params.c) {
                // si hay un filtro erroneo cargar filtro por defecto
                throw 'El param ingresado no es valido'
              }

              // ----- fecha
              this.setupWeek(Number(params.f));

              // ----- acopio
              // seleccionamos el acopio desde los acopios en cache
              if (!this.listaAcopios.length && this.listaAcopios.some(x => x.Id.toString() === params.a)) {
                throw 'No se econtró el acopio';
              }
              // usamos el emit event false para evitar que el handler de cambios se dispare (en el contsructor)
              this.acopioSelectedCtrl.setValue(this.listaAcopios.find(x => x.Id.toString() === params.a), {emitEvent: false});

              // ----- centro emisor
              // una vez seleccionado debemos cargar los centros emisores
              const sub = this.remitosService.getCentrosEmisores(this.acopioSelected.Id).subscribe(
                (data) => {
                  // dado que es una funcion asincronica, debemos usar el trycatch nuevamente
                  try {
                    if (!data || !data.length) {
                      throw 'No se encontraron centros emisores';
                    }
                    this.listaCentrosEmisores = data.map(x => ({
                      Label: x.CentroEmisorId, Id: x.CentroEmisorId, RawValue: JSON.stringify(x)
                    }));

                    if (!this.listaCentrosEmisores.some(x => x.Id.toString() === params.c)) {
                      throw 'No se encontró el centro emisor';
                    }

                    this.centroEmisorSelectedCtrl.setValue(
                      this.listaCentrosEmisores.find(x => x.Id.toString() === params.c),
                      {emitEvent: false}
                    );

                    // solo los admin pueden seleccionar el acopio y centro emisor
                    if (this._isAdmin) {
                      this.form.enable({onlySelf: false, emitEvent: false})
                    }
                    this.formInitializing = false;
                    this.onSubmit();
                    sub.unsubscribe();
                  } catch (err) {
                    this.loadDefaultFilter(true);
                  }
                }
              );
            } catch (err) {
              this.loadDefaultFilter(true);
            }
          } else {
            this.loadDefaultFilter();
          }
        });
      },
      error => {
        console.error('Ha ocurrido un error al obtener la informacion de')
      }
    );
  }

  loadDefaultFilter(sendErrorMessage = false) {
    this.setupWeek(moment().unix());
    // se llega hasta esta funcion presuponiendo que los acopios y los valores de JDE fueron cargados
    if (this.listaAcopios.some(x => x.Id.toString() === this.jdeData[0].Oficina_Comercial.Id.toString())) {
      this.acopioSelectedCtrl.setValue(
        this.listaAcopios.find(x => x.Id.toString() === this.jdeData[0].Oficina_Comercial.Id.toString()),
        {emitEvent: false}
      );
      // una vez seleccionado el acopio, seleccionamos el centro emisor a partir del mismo
      const sub = this.remitosService.getCentrosEmisores(this.acopioSelected.Id).subscribe(
        (data) => {
          if (data && data.length) {
            this.listaCentrosEmisores = data.map(x => ({
              Label: x.CentroEmisorId, Id: x.CentroEmisorId, RawValue: JSON.stringify(x)
            }));

            this.centroEmisorSelectedCtrl.setValue(
              this.listaCentrosEmisores[0],
              {emitEvent: false}
            );

            // solo los admin pueden seleccionar el acopio y centro emisor
            if (this._isAdmin) {
              this.form.enable({onlySelf: false, emitEvent: false})
            }
            this.formInitializing = false;
            this.onSubmit();
          } else {
            this.snackBar.open(
              'Se ha producido un error al procesar la solicitud',
              null,
              {
                duration: 5000,
              }
            );
          }
          sub.unsubscribe();
        }
      );
    }

  }

  searchCentroEmisor(idAcopio: number) {
    if (this.formInitializing) {
      return;
    }
    // luego de seleccionar un acopio, realizamos una busqueda de los centros emisores
    this.emisoresLoading = true;
    this.centroEmisorSelectedCtrl.setValue(null, {emitEvent: false});
    const sub = this.remitosService.getCentrosEmisores(idAcopio).subscribe(
      (data) => {
        this.listaCentrosEmisores = data.map(x => ({Label: x.CentroEmisorId, Id: x.CentroEmisorId, RawValue: JSON.stringify(x)}));
        if (this.listaCentrosEmisores.length === 1) {
          this.centroEmisorSelectedCtrl.setValue(this.listaCentrosEmisores[0], {emitEvent: false});
          this.onSubmit();
        }
        if (this.centroEmisorSelected === null) {
          this.invalidFilter.emit();
        }
        this.emisoresLoading = false;
        sub.unsubscribe();
      },
      (err) => {
        this.emisoresLoading = false;
      }
    );
  }

  get acopioSelected(): any {
    return this.form.get('acopioSelected').value;
  }

  get acopioSelectedCtrl(): AbstractControl {
    return this.form.get('acopioSelected');
  }

  get centroEmisorSelected(): any {
    return this.form.get('centroEmisorSelected').value;
  }

  get centroEmisorSelectedCtrl(): AbstractControl {
    return this.form.get('centroEmisorSelected');
  }

  emisoresPlaceholder() {
    if (this.emisoresLoading) {
      return 'Cargando...';
    }
    return this.acopioSelected === null || this.listaCentrosEmisores.length > 0
      ? 'Seleccione un Centro Emisor'
      : 'No se encontraron centros emisores';
  }

  //region Fecha
  setupWeek(startDate: number) {
    // prepara el array semanal a partir de la fecha especificada
    this.week = [];
    for (let i = 6; i > -1; i--) {
      const currDate = moment.unix(startDate).subtract(i, 'days');
      const item: DayObj = {
        label: currDate.locale('es').format('LLLL').slice(0, 3).toUpperCase(),
        number: currDate.date(),
        containsData: false,
        fullDate: currDate.format('DD-MM-YYYY'),
        // tomamos la distancia (i-4) entre el index y 4 usandolo como punto medio y la sumamos a 3
        // para obtener un valor de index invertido respecto a i (ej i=0, index=6; i=1, index=5)
        index: 3 + (i - 3) * -1,
      };
      this.week.push(item);
    }
    this.selectDay(this.week[6]); // por defecto seleccionamos el ultimo dia
  }

  selectDay(day: DayObj): void {
    if (
      !this.selectedDay ||
      (this.selectedDay && this.selectedDay.fullDate !== day.fullDate)
    ) {
      this.selectedDay = day;
      this.matDatepickerDate = moment(
        this.selectedDay.fullDate,
        'DD-MM-YYYY'
      ).toDate();
      this.onSubmit();
    }
  }

  isSelected(day: DayObj): boolean {
    return this.selectedDay !== null
      ? this.selectedDay.fullDate === day.fullDate
      : false;
  }

  setFirstDate(e) {
    // checkeamos si la fecha se encuentra en el array actual

    const selectedDate = moment(e).format('DD-MM-YYYY');
    if (this.week.some((x) => x.fullDate === selectedDate)) {
      // la fecha se ecuentra en el array, solo la seleccionamos
      this.selectDay(this.week.find((x) => x.fullDate === selectedDate));
    } else {
      // la fecha no se encuentra en el array, generamos un nuevo array de fechas
      this.setupWeek(moment(e).unix());
    }
  }
  //endregion

  onSubmit() {
    // solo vamos a enviar un evento con el filtro en caso de que todos los filtros sean validos.
    if (this.acopioSelected !== null && this.centroEmisorSelected !== null) {
      this.filterChanged.emit({
        date: moment(this.selectedDay.fullDate, 'DD-MM-YYYY')
          .toDate()
          .toString(),
        acopio: this.acopioSelected,
        centroEmisor: this.centroEmisorSelected,
        valid: true,
      });
      this.setQueryParams();
    }
  }

  setQueryParams() {
    // actualizamos los params
    const params = new HttpParams()
      .append('f', moment(this.selectedDay.fullDate, 'DD-MM-YYYY').unix().toString())
      .append('a', this.acopioSelected.Id)
      .append('c', this.centroEmisorSelected.Id);
    this.location.go(this.location.path().split('?')[0], params.toString())
  }

}

interface DayObj {
  label: string;
  number: number;
  containsData: boolean;
  fullDate: string;
  index: number;
}

export interface RemitoRequiredFilterObj {
  date: string;
  acopio: FormSelectItem;
  centroEmisor: FormSelectItem;
  valid: boolean;
}

interface RemitoFilterParams {
  // usamos los keys abreviados para evitar que se muestren los nombres tal cual en la URL
  // fecha
  f: string;
  // acopio
  a: string;
  // centro emisor
  c: string;
}



// WEBPACK FOOTER //
// E:/jenkins_home/workspace/_agenda-unco-front-legacy_master/frontend-web/src/app/components/administracion/remitos/common/remitos-fecha-selector/remitos-fecha-selector.component.ts